/**
 * GRANULLO EXPOSURE ENGINE — Core Calculator
 * 
 * Implements the exposure scoring methodology:
 * Exposure Score = (Dependency Depth × Cascade Reach × Commitment Weight × Fragility) / Normalizer
 * 
 * This engine reveals structural exposure, not predictions.
 * All outputs are advisory and non-executing.
 */

// Normalizer calibrated to maintain 0-100 scale
const EXPOSURE_NORMALIZER = 0.06;

// Classification thresholds
const EXPOSURE_THRESHOLDS = {
  LOW: 30,
  MEDIUM: 60,
  HIGH: 80,
  CRITICAL: 100
};

/**
 * Calculate Dependency Depth score
 * @param {number} directDeps - Direct dependencies count
 * @param {number} indirectDeps - Indirect dependencies count
 * @returns {Object} Dependency depth analysis
 */
export function calculateDependencyDepth(directDeps, indirectDeps) {
  const total = directDeps + (indirectDeps * 0.5);
  
  let classification, multiplier;
  if (total <= 2) {
    classification = 'shallow';
    multiplier = 1.0;
  } else if (total <= 5) {
    classification = 'moderate';
    multiplier = 1.5;
  } else if (total <= 10) {
    classification = 'deep';
    multiplier = 2.0;
  } else {
    classification = 'critical';
    multiplier = 3.0;
  }
  
  return {
    direct: directDeps,
    indirect: indirectDeps,
    total: Math.round(total * 10) / 10,
    classification,
    multiplier
  };
}

/**
 * Calculate Cascade Reach score
 * @param {number} maxHops - Maximum propagation depth
 * @param {number} affectedNodes - Number of affected nodes
 * @returns {Object} Cascade reach analysis
 */
export function calculateCascadeReach(maxHops, affectedNodes) {
  let classification, multiplier;
  if (maxHops <= 1) {
    classification = 'local';
    multiplier = 1.0;
  } else if (maxHops <= 3) {
    classification = 'regional';
    multiplier = 1.5;
  } else if (maxHops <= 6) {
    classification = 'network-wide';
    multiplier = 2.0;
  } else {
    classification = 'systemic';
    multiplier = 3.0;
  }
  
  return {
    maxHops,
    affectedNodes,
    classification,
    multiplier
  };
}

/**
 * Calculate Commitment Weight score
 * @param {number} economicValue - Economic value in USD
 * @returns {Object} Commitment weight analysis
 */
export function calculateCommitmentWeight(economicValue) {
  const normalizedWeight = Math.log10(economicValue + 1) / 8;
  
  let classification, multiplier;
  if (economicValue < 1000000) {
    classification = 'minor';
    multiplier = 1.0;
  } else if (economicValue < 10000000) {
    classification = 'moderate';
    multiplier = 1.5;
  } else if (economicValue < 100000000) {
    classification = 'significant';
    multiplier = 2.0;
  } else {
    classification = 'critical';
    multiplier = 3.0;
  }
  
  return {
    economicValue,
    normalizedWeight: Math.round(normalizedWeight * 100) / 100,
    classification,
    multiplier
  };
}

/**
 * Calculate Fragility score
 * @param {number} integrityScore - Integrity score (0-100)
 * @param {number} failingDeps - Number of failing dependencies
 * @param {number} totalDeps - Total dependencies
 * @returns {Object} Fragility analysis
 */
export function calculateFragility(integrityScore, failingDeps, totalDeps) {
  const integrityComponent = 1 - (integrityScore / 100);
  const dependencyComponent = totalDeps > 0 ? (failingDeps / totalDeps) * 0.5 : 0;
  const score = Math.min(1, Math.max(0, integrityComponent + dependencyComponent));
  
  let classification;
  if (score <= 0.3) {
    classification = 'resilient';
  } else if (score <= 0.6) {
    classification = 'standard';
  } else if (score <= 0.8) {
    classification = 'elevated';
  } else {
    classification = 'fragile';
  }
  
  return {
    score: Math.round(score * 100) / 100,
    integrityScore,
    failingDependencies: failingDeps,
    totalDependencies: totalDeps,
    classification
  };
}

/**
 * Calculate full Exposure Score
 * @param {Object} params - Calculation parameters
 * @returns {number} Exposure score (0-100)
 */
export function calculateExposureScore(dependencyDepth, cascadeReach, commitmentWeight, fragility) {
  const rawScore = 
    dependencyDepth.multiplier * 
    cascadeReach.multiplier * 
    commitmentWeight.multiplier * 
    fragility.score;
  
  // Normalize to 0-100 scale
  const normalizedScore = Math.min(100, Math.max(0, rawScore / EXPOSURE_NORMALIZER));
  
  return Math.round(normalizedScore);
}

/**
 * Calculate Cascade Probability
 * @param {number} exposureScore - Exposure score (0-100)
 * @param {number} fragilityScore - Fragility score (0-1)
 * @param {number} cascadeReachHops - Max cascade hops
 * @returns {number} Cascade probability (0-100%)
 */
export function calculateCascadeProbability(exposureScore, fragilityScore, cascadeReachHops) {
  const probability = (exposureScore / 100) * fragilityScore * (cascadeReachHops / 10);
  return Math.round(Math.min(100, Math.max(0, probability * 100)) * 10) / 10;
}

/**
 * Calculate Economic Exposure
 * @param {number} commitmentValue - Base commitment value
 * @param {number} cascadeProbability - Cascade probability (0-100%)
 * @param {number} dependencyDepthTotal - Total dependency depth
 * @returns {number} Economic exposure in USD
 */
export function calculateEconomicExposure(commitmentValue, cascadeProbability, dependencyDepthTotal) {
  const exposure = commitmentValue * (cascadeProbability / 100) * (1 + dependencyDepthTotal / 10);
  return Math.round(exposure);
}

/**
 * Get exposure classification from score
 * @param {number} score - Exposure score (0-100)
 * @returns {string} Classification label
 */
export function getExposureClassification(score) {
  if (score <= EXPOSURE_THRESHOLDS.LOW) return 'low';
  if (score <= EXPOSURE_THRESHOLDS.MEDIUM) return 'medium';
  if (score <= EXPOSURE_THRESHOLDS.HIGH) return 'high';
  return 'critical';
}

/**
 * Get exposure color based on score
 * @param {number} score - Exposure score (0-100)
 * @returns {Object} Color configuration
 */
export function getExposureColor(score) {
  if (score <= EXPOSURE_THRESHOLDS.LOW) {
    return { bg: 'rgba(63, 185, 80, 0.1)', text: '#3fb950', glow: '#3fb950' };
  }
  if (score <= EXPOSURE_THRESHOLDS.MEDIUM) {
    return { bg: 'rgba(240, 136, 62, 0.1)', text: '#f0883e', glow: '#f0883e' };
  }
  if (score <= EXPOSURE_THRESHOLDS.HIGH) {
    return { bg: 'rgba(248, 81, 73, 0.1)', text: '#f85149', glow: '#f85149' };
  }
  return { bg: 'rgba(218, 54, 51, 0.1)', text: '#da3633', glow: '#da3633' };
}

/**
 * Generate primary risk nodes from commitment data
 * @param {Object} commitment - Commitment data
 * @param {Object} exposureAnalysis - Exposure analysis results
 * @returns {Array} Primary risk nodes
 */
export function generatePrimaryRiskNodes(commitment, exposureAnalysis) {
  const risks = [];
  
  // Analyze fragility
  if (exposureAnalysis.fragility.classification === 'fragile' || exposureAnalysis.fragility.classification === 'elevated') {
    risks.push({
      nodeId: `risk-integrity-${commitment.id}`,
      label: 'Integrity degradation',
      riskType: 'integrity',
      severity: exposureAnalysis.fragility.classification === 'fragile' ? 'critical' : 'high',
      description: `Integrity score at ${exposureAnalysis.fragility.integrityScore}% with ${exposureAnalysis.fragility.failingDependencies} failing dependencies`
    });
  }
  
  // Analyze cascade reach
  if (exposureAnalysis.cascadeReach.classification === 'systemic') {
    risks.push({
      nodeId: `risk-cascade-${commitment.id}`,
      label: 'Systemic cascade risk',
      riskType: 'dependency',
      severity: 'high',
      description: `Consequences propagate ${exposureAnalysis.cascadeReach.maxHops} hops affecting ${exposureAnalysis.cascadeReach.affectedNodes} nodes`
    });
  }
  
  // Analyze dependency depth
  if (exposureAnalysis.dependencyDepth.classification === 'critical') {
    risks.push({
      nodeId: `risk-dependency-${commitment.id}`,
      label: 'Critical dependency chain',
      riskType: 'dependency',
      severity: 'high',
      description: `${exposureAnalysis.dependencyDepth.direct} direct and ${exposureAnalysis.dependencyDepth.indirect} indirect dependencies`
    });
  }
  
  // Analyze commitment weight
  if (exposureAnalysis.commitmentWeight.classification === 'critical') {
    risks.push({
      nodeId: `risk-economic-${commitment.id}`,
      label: 'High economic concentration',
      riskType: 'economic',
      severity: 'medium',
      description: `$${(exposureAnalysis.commitmentWeight.economicValue / 1000000).toFixed(1)}M value concentration`
    });
  }
  
  // Add regulatory risk for certain categories
  if (commitment.category === 'REGULATORY' || commitment.category === 'COMPLIANCE') {
    risks.push({
      nodeId: `risk-regulatory-${commitment.id}`,
      label: 'Regulatory dependency',
      riskType: 'regulatory',
      severity: 'medium',
      description: 'Subject to regulatory approval and compliance requirements'
    });
  }
  
  // Sort by severity and limit to top 5
  const severityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
  return risks
    .sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity])
    .slice(0, 5);
}

/**
 * Calculate complete exposure profile for a commitment
 * @param {Object} commitment - Commitment data
 * @param {Object} networkContext - Network context for cascade calculation
 * @returns {Object} Complete ExposureProfile
 */
export function calculateExposureProfile(commitment, networkContext = {}) {
  // Extract or estimate values from commitment
  const directDeps = commitment.dependencies?.length || Math.floor(Math.random() * 15) + 3;
  const indirectDeps = Math.floor(directDeps * 2.5);
  const maxHops = Math.min(10, Math.floor(directDeps / 2) + 2);
  const affectedNodes = Math.floor(directDeps * 1.5);
  const economicValue = commitment.amount || commitment.value || 50000000;
  const integrityScore = commitment.integrityScore || commitment.integrity || 75;
  const failingDeps = Math.floor(directDeps * 0.1);
  const totalDeps = directDeps + indirectDeps;
  
  // Calculate component scores
  const dependencyDepth = calculateDependencyDepth(directDeps, indirectDeps);
  const cascadeReach = calculateCascadeReach(maxHops, affectedNodes);
  const commitmentWeight = calculateCommitmentWeight(economicValue);
  const fragility = calculateFragility(integrityScore, failingDeps, totalDeps);
  
  // Calculate derived metrics
  const exposureScore = calculateExposureScore(dependencyDepth, cascadeReach, commitmentWeight, fragility);
  const cascadeProbability = calculateCascadeProbability(exposureScore, fragility.score, cascadeReach.maxHops);
  const economicExposure = calculateEconomicExposure(economicValue, cascadeProbability, dependencyDepth.total);
  
  // Generate risk nodes
  const exposureAnalysis = { dependencyDepth, cascadeReach, commitmentWeight, fragility };
  const primaryRiskNodes = generatePrimaryRiskNodes(commitment, exposureAnalysis);
  
  return {
    commitmentId: commitment.id || commitment._id,
    gciId: commitment.gciId || commitment.id,
    
    // Core Scores
    exposureScore,
    cascadeProbability,
    economicExposure,
    classification: getExposureClassification(exposureScore),
    
    // Component Variables
    dependencyDepth,
    cascadeReach,
    commitmentWeight,
    fragility,
    
    // Risk Nodes
    primaryRiskNodes,
    
    // Metadata
    calculatedAt: new Date().toISOString(),
    version: '1.0.0'
  };
}

/**
 * Calculate network-wide exposure state
 * @param {Array} exposureProfiles - Array of ExposureProfile objects
 * @returns {Object} NetworkExposureState
 */
export function calculateNetworkExposureState(exposureProfiles) {
  if (!exposureProfiles || exposureProfiles.length === 0) {
    return {
      totalExposure: 0,
      averageExposureScore: 0,
      highExposureCount: 0,
      criticalExposureCount: 0,
      exposureDistribution: { low: 0, medium: 0, high: 0, critical: 0 },
      topExposureCommitments: [],
      calculatedAt: new Date().toISOString()
    };
  }
  
  const totalExposure = exposureProfiles.reduce((sum, p) => sum + p.economicExposure, 0);
  const averageExposureScore = Math.round(
    exposureProfiles.reduce((sum, p) => sum + p.exposureScore, 0) / exposureProfiles.length
  );
  
  const distribution = { low: 0, medium: 0, high: 0, critical: 0 };
  exposureProfiles.forEach(p => {
    distribution[p.classification]++;
  });
  
  const highExposureCount = distribution.high + distribution.critical;
  const criticalExposureCount = distribution.critical;
  
  // Sort by exposure score and get top 10
  const topExposureCommitments = [...exposureProfiles]
    .sort((a, b) => b.exposureScore - a.exposureScore)
    .slice(0, 10);
  
  return {
    totalExposure,
    averageExposureScore,
    highExposureCount,
    criticalExposureCount,
    exposureDistribution: distribution,
    topExposureCommitments,
    calculatedAt: new Date().toISOString()
  };
}

/**
 * Calculate exposure delta between two states
 * @param {Object} beforeState - Network state before change
 * @param {Object} afterState - Network state after change
 * @returns {Object} Exposure delta
 */
export function calculateExposureDelta(beforeState, afterState) {
  return {
    totalExposureDelta: afterState.totalExposure - beforeState.totalExposure,
    averageScoreDelta: afterState.averageExposureScore - beforeState.averageExposureScore,
    highExposureCountDelta: afterState.highExposureCount - beforeState.highExposureCount,
    direction: afterState.totalExposure > beforeState.totalExposure ? 'increased' : 'decreased'
  };
}

export const EXPOSURE_THRESHOLDS_CONFIG = EXPOSURE_THRESHOLDS;
